import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, messageForCode } from '../lib/api-error';
import { useAuth } from '../lib/auth-context';
import { useContacts } from '../lib/contacts-api';
import { date, dateTime, money } from '../lib/format';
import { useItems } from '../lib/items-api';
import { isAdmin } from '../lib/permissions';
import {
  SNAK_BILLING_MODE_LABELS,
  SNAK_FEE_BILLING_LABELS,
  SNAK_STATEMENT_STATUS_LABELS,
  SNAK_VAT_MODE_LABELS,
  type SnakBillingMode,
  type SnakFeeBilling,
  type SnakTenant,
  type SnakVatMode,
  useAddSnakTerms,
  useCreateSnakTenant,
  useDeleteSnakTenant,
  useRetrySnakStatement,
  useRotateSnakSecret,
  useRunSnakNow,
  useSnakStatements,
  useSnakTenants,
  useUpdateSnakTenant,
} from '../lib/snak-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * snak ENTEGRASYONU — restoranların aylık platform faturaları.
 *
 * ⚠⚠ BU EKRANIN ASIL İŞİ BEKLETİLENLERİ GÖSTERMEK. Faturalar kendiliğinden
 * kesilir ve gönderilir; insanın burada yapacağı şey, bir kapıya takılıp
 * KESİLMEMİŞ ayları görmek ve sebebini gidermektir. Bu yüzden bekletilenler
 * en üstte ve kırmızı — altta bir rozet, operatörün göremeyeceği tek yerdir.
 *
 * ⚠ YAZMA YALNIZ YÖNETİCİ: restoran kaydı bir imza sırrı üretir, koşul satırı
 * otomatik kesilecek faturanın FİYATIDIR. Sunucu zaten reddeder; düğmeler
 * yalnız yanlış izlenim vermesin diye gizli.
 */
export function SnakIntegrationPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const tenants = useSnakTenants();
  const statements = useSnakStatements();
  const retry = useRetrySnakStatement();
  const run = useRunSnakNow();
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [yeniAcik, setYeniAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState<SnakTenant | null>(null);
  const [kosulIcin, setKosulIcin] = useState<SnakTenant | null>(null);
  const [sir, setSir] = useState<{ tenantKey: string; secret: string } | null>(null);

  const rows = statements.data ?? [];
  const bekleyen = rows.filter((r) => r.status === 'HELD');
  const iletilmemis = rows.filter((r) => r.status === 'INVOICED' && !r.delivered);

  return (
    <>
      <PageHeader
        title="snak faturaları"
        description="Restoranların aylık platform ekstreleri. Kapılar tutarsa fatura kendiliğinden kesilir ve e-postayla gönderilir; tutmazsa ay burada bekler."
        actions={
          admin ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                loading={run.isPending}
                onClick={() => {
                  setHata(null);
                  setBilgi(null);
                  run.mutate(undefined, {
                    onSuccess: (r) =>
                      setBilgi(
                        r.busy
                          ? 'Koşum zaten çalışıyor.'
                          : `Yeniden değerlendirilen: ${r.reevaluated ?? 0} · kesilen: ${r.invoiced ?? 0} · yeniden iletilen: ${r.redelivered ?? 0}`,
                      ),
                    onError: (e: unknown) => setHata(errorMessage(e)),
                  });
                }}
              >
                Şimdi çalıştır
              </Button>
              <Button onClick={() => setYeniAcik(true)}>Restoran ekle</Button>
            </div>
          ) : undefined
        }
      />

      {hata && <p className="mt-3 text-sm text-[var(--c-danger)]">{hata}</p>}
      {bilgi && <p className="mt-3 text-sm text-[var(--c-ink-soft)]">{bilgi}</p>}

      {/*
        ⚠ SIR YALNIZ BİR KEZ GÖRÜNÜR. Sunucu onu şifreli saklar ve bir daha
        hiçbir uçta döndürmez; kaybedilirse tek yol döndürmektir.
      */}
      {sir && (
        <Card className="mt-4 border-[var(--c-warn)]">
          <div className="space-y-2 px-4 py-3 text-sm">
            <p>
              <strong>{sir.tenantKey}</strong> için imza sırrı — <strong>yalnız şimdi</strong>{' '}
              görünür. snak sunucusunun ortam dosyasına yazın:
            </p>
            <pre className="overflow-x-auto rounded bg-[var(--c-surface-alt)] p-2 text-xs">
              {`MUHASEBE_TENANT_KEY=${sir.tenantKey}\nMUHASEBE_SECRET=${sir.secret}`}
            </pre>
            <Button variant="secondary" size="sm" onClick={() => setSir(null)}>
              Yazdım, gizle
            </Button>
          </div>
        </Card>
      )}

      {bekleyen.length > 0 && (
        <Card className="mt-4 border-[var(--c-danger)]">
          <CardHeader
            title={`Bekletilen aylar — ${bekleyen.length} fatura KESİLMEDİ`}
            description="Faturanın son günü dönemi izleyen ayın 15'idir. Sebep giderildiğinde ekstre her sabah kendiliğinden yeniden denenir."
          />
          <Table>
            <thead>
              <tr>
                <Th>Restoran · ay</Th>
                <Th>Sebep</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {bekleyen.map((r) => (
                <tr key={r.id}>
                  <Td>
                    <span className="text-sm font-medium">{r.displayName}</span>
                    <br />
                    <span className="text-xs text-[var(--c-ink-soft)]">
                      {r.periodLabel} · {r.statementRef}
                      {r.regularization ? ' · düzeltme faturası' : ''}
                    </span>
                  </Td>
                  <Td>
                    {r.holdReasons.length === 0 ? (
                      <span className="text-xs text-[var(--c-ink-soft)]">sebep yazılmadı</span>
                    ) : (
                      r.holdReasons.map((reason, i) => (
                        <p key={i} className="text-xs">
                          <span className="font-mono text-[var(--c-danger)]">{reason.code}</span>
                          {reason.detail ? ` — ${reason.detail}` : ''}
                          <br />
                          <span className="text-[var(--c-ink-soft)]">
                            {messageForCode(reason.code) ?? 'Panelde bu kodun açıklaması yok.'}
                          </span>
                        </p>
                      ))
                    )}
                  </Td>
                  <Td>
                    {admin && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={retry.isPending}
                        onClick={() => {
                          setHata(null);
                          retry.mutate(r.id, { onError: (e: unknown) => setHata(errorMessage(e)) });
                        }}
                      >
                        Yeniden dene
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {iletilmemis.length > 0 && (
        <Card className="mt-4 border-[var(--c-warn)]">
          <CardHeader
            title={`Kesildi ama müşteriye İLETİLMEDİ — ${iletilmemis.length}`}
            description="Fatura yasal olarak kesildi ve geri alınmaz. İletim her sabah yeniden denenir; e-fatura ekranından elle de gönderilebilir."
          />
          <ul className="px-4 pb-3 text-sm">
            {iletilmemis.map((r) => (
              <li key={r.id}>
                {r.displayName} · {r.periodLabel} ·{' '}
                {r.invoiceId ? (
                  <Link className="underline" to={`/einvoice?invoice=${r.invoiceId}`}>
                    {r.invoiceNumber}
                  </Link>
                ) : (
                  r.invoiceNumber
                )}
                {r.deliveryError ? ` — ${messageForCode(r.deliveryError) ?? r.deliveryError}` : ''}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader
          title="Restoranlar ve sözleşme koşulları"
          description="Ücret ve komisyon oranı BURADAN gelir; snak'ın bildirdiği rakam yalnız karşılaştırılır. Zam ya da oran değişikliği yeni bir koşul satırıdır."
        />
        {tenants.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : tenants.isError ? (
          <ErrorState message={errorMessage(tenants.error)} />
        ) : (tenants.data ?? []).length === 0 ? (
          <EmptyState
            title="Henüz restoran yok"
            description="Her snak kurulumu bir restoran kaydıdır: anahtar + imza sırrı snak sunucusuna yazılır."
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Restoran</Th>
                <Th>Yürürlükteki koşul</Th>
                <Th>Son ekstre</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {(tenants.data ?? []).map((t) => {
                const guncel = t.terms[0];
                return (
                  <tr key={t.id}>
                    <Td>
                      <span className="text-sm font-medium">{t.displayName}</span>{' '}
                      {!t.enabled && <Badge tone="neutral">kapalı</Badge>}
                      <br />
                      <span className="font-mono text-xs text-[var(--c-ink-soft)]">
                        {t.tenantKey}
                      </span>
                      {t.hasPreviousSecret && (
                        <p className="text-xs text-[var(--c-warn)]">
                          ⚠ Eski sır hâlâ geçerli — snak sunucusu yeni sırla güncellenince bir kez
                          daha döndürün.
                        </p>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {guncel ? (
                        <>
                          {SNAK_BILLING_MODE_LABELS[guncel.billingMode]} ·{' '}
                          {date(guncel.effectiveFrom)}
                          ’den
                          <br />
                          ücret {money(guncel.monthlyFeeNet)} (KDV hariç)
                          {guncel.listPriceNet
                            ? ` — liste ${money(guncel.listPriceNet)}, fark indirim`
                            : ''}{' '}
                          · komisyon %{guncel.commissionPercent}
                          <br />
                          <span className="text-[var(--c-ink-soft)]">
                            Ücret: {SNAK_FEE_BILLING_LABELS[t.feeBilling]}
                          </span>
                          <br />
                          <span className="text-[var(--c-ink-soft)]">
                            {SNAK_VAT_MODE_LABELS[guncel.vatMode]}
                          </span>
                        </>
                      ) : (
                        <span className="text-[var(--c-danger)]">
                          Koşul yok — ekstre gelirse bekletilir
                        </span>
                      )}
                    </Td>
                    <Td className="text-xs">
                      {t.lastStatementAt ? dateTime(t.lastStatementAt) : '—'}
                    </Td>
                    <Td>
                      {admin && (
                        <div className="flex flex-wrap gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setKosulIcin(t)}>
                            Koşul ekle
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDuzenlenen(t)}>
                            Düzenle
                          </Button>
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Card className="mt-4">
        <CardHeader title="Gelen ekstreler" />
        {statements.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : statements.isError ? (
          <ErrorState message={errorMessage(statements.error)} />
        ) : rows.length === 0 ? (
          <EmptyState title="Henüz ekstre gelmedi" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ay</Th>
                <Th>Restoran</Th>
                <Th>Durum</Th>
                <Th>Fatura</Th>
                <Th align="right">Toplam</Th>
                <Th align="right">Önceden alınan</Th>
                <Th align="right">Kalan</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <Td className="text-xs">{r.periodLabel}</Td>
                  <Td className="text-xs">{r.displayName}</Td>
                  <Td>
                    <Badge
                      tone={r.status === 'INVOICED' ? (r.delivered ? 'ok' : 'warn') : 'danger'}
                    >
                      {SNAK_STATEMENT_STATUS_LABELS[r.status]}
                    </Badge>
                  </Td>
                  <Td className="text-xs">
                    {r.invoiceId ? (
                      <Link className="underline" to={`/invoices/${r.invoiceId}`}>
                        {r.invoiceNumber}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td align="right">{r.totalInclVat ? money(r.totalInclVat) : '—'}</Td>
                  <Td align="right">{r.prepaidAmount ? money(r.prepaidAmount) : '—'}</Td>
                  <Td align="right">{r.payableAmount ? money(r.payableAmount) : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <TenantModal
        open={yeniAcik || duzenlenen !== null}
        tenant={duzenlenen}
        onClose={() => {
          setYeniAcik(false);
          setDuzenlenen(null);
        }}
        onSecret={(s) => setSir(s)}
      />
      <TermsModal tenant={kosulIcin} onClose={() => setKosulIcin(null)} />
    </>
  );
}

function TenantModal({
  open,
  tenant,
  onClose,
  onSecret,
}: {
  open: boolean;
  tenant: SnakTenant | null;
  onClose: () => void;
  onSecret: (s: { tenantKey: string; secret: string }) => void;
}) {
  const contacts = useContacts({ isCustomer: true, active: true, limit: 200 });
  const items = useItems({ active: true, limit: 200 });
  const create = useCreateSnakTenant();
  const update = useUpdateSnakTenant();
  const rotate = useRotateSnakSecret();
  const remove = useDeleteSnakTenant();
  const [form, setForm] = useState<{
    tenantKey: string;
    displayName: string;
    contactId: string;
    feeItemId: string;
    commissionItemId: string;
    paymentTermDays: string;
    enabled: boolean;
    feeBilling: SnakFeeBilling;
  } | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  /*
   * Form, modal açıldığı anda seçili kayıttan kurulur; kayıt değişirse
   * (başka restoran seçildi) yeniden kurulur.
   */
  const anahtar = tenant?.id ?? (open ? 'yeni' : null);
  const [kurulan, setKurulan] = useState<string | null>(null);
  if (anahtar !== kurulan) {
    setKurulan(anahtar);
    setHata(null);
    setForm(
      anahtar === null
        ? null
        : {
            tenantKey: tenant?.tenantKey ?? '',
            displayName: tenant?.displayName ?? '',
            contactId: tenant?.contactId ?? '',
            feeItemId: tenant?.feeItemId ?? '',
            commissionItemId: tenant?.commissionItemId ?? '',
            paymentTermDays: String(tenant?.paymentTermDays ?? 14),
            feeBilling: tenant?.feeBilling ?? 'ADVANCE',
            enabled: tenant?.enabled ?? true,
          },
    );
  }
  if (!open || form === null) return null;

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const kaydet = () => {
    setHata(null);
    const common = {
      displayName: form.displayName,
      contactId: form.contactId,
      feeItemId: form.feeItemId || null,
      commissionItemId: form.commissionItemId || null,
      paymentTermDays: Number(form.paymentTermDays),
      feeBilling: form.feeBilling,
    };
    if (tenant) {
      update.mutate(
        { id: tenant.id, ...common, enabled: form.enabled },
        { onSuccess: onClose, onError: (e: unknown) => setHata(errorMessage(e)) },
      );
    } else {
      create.mutate(
        { tenantKey: form.tenantKey, ...common },
        {
          onSuccess: (r) => {
            onSecret({ tenantKey: r.tenant.tenantKey, secret: r.secret });
            onClose();
          },
          onError: (e: unknown) => setHata(errorMessage(e)),
        },
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={tenant ? `${tenant.displayName} — düzenle` : 'Restoran ekle'}
      footer={
        <>
          {/*
            ⚠ SİL yalnız yanlış açılmış kayıt içindir (anahtar ve cari sonradan
            değiştirilemez). Ekstre almış kaydı sunucu reddeder; yolu "Durum:
            Kapalı"dır. Kural sunucuda, düğme yalnız sorar.
          */}
          {tenant && (
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `"${tenant.displayName}" (${tenant.tenantKey}) kaydı ve koşulları silinsin mi? Geri alınamaz. Ekstre almış bir kayıt silinmez.`,
                  )
                ) {
                  return;
                }
                setHata(null);
                remove.mutate(tenant.id, {
                  onSuccess: onClose,
                  onError: (e: unknown) => setHata(errorMessage(e)),
                });
              }}
            >
              Sil
            </Button>
          )}
          {tenant && (
            <Button
              variant="ghost"
              loading={rotate.isPending}
              onClick={() => {
                setHata(null);
                rotate.mutate(tenant.id, {
                  onSuccess: (r) => {
                    onSecret({ tenantKey: tenant.tenantKey, secret: r.secret });
                    onClose();
                  },
                  onError: (e: unknown) => setHata(errorMessage(e)),
                });
              }}
            >
              Sırrı döndür
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={create.isPending || update.isPending} onClick={kaydet}>
            Kaydet
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!tenant && (
          <Input
            label="Restoran anahtarı"
            hint="snak sunucusunun MUHASEBE_TENANT_KEY değeri. Küçük harf, rakam, tire. Sonradan değişmez."
            value={form.tenantKey}
            onChange={(e) => set({ tenantKey: e.target.value })}
          />
        )}
        <Input
          label="Görünen ad"
          value={form.displayName}
          onChange={(e) => set({ displayName: e.target.value })}
        />
        <Select
          label="Cari (faturanın alıcısı)"
          hint="snak'ın bildirdiği KDV numarası bu carininkiyle tutmak zorunda; tutmazsa fatura kesilmez."
          value={form.contactId}
          onChange={(e) => set({ contactId: e.target.value })}
        >
          <option value="">— seçin —</option>
          {(contacts.data?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
              {c.vatNumber ? ` · ${c.vatNumber}` : ''}
            </option>
          ))}
        </Select>
        <Select
          label="Aylık ücret kalemi"
          value={form.feeItemId}
          onChange={(e) => set({ feeItemId: e.target.value })}
        >
          <option value="">— yok —</option>
          {(items.data?.items ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.name.fr}
            </option>
          ))}
        </Select>
        <Select
          label="Komisyon kalemi"
          value={form.commissionItemId}
          onChange={(e) => set({ commissionItemId: e.target.value })}
        >
          <option value="">— yok —</option>
          {(items.data?.items ?? []).map((i) => (
            <option key={i.id} value={i.id}>
              {i.name.fr}
            </option>
          ))}
        </Select>
        <Input
          label="Ödeme vadesi (gün)"
          type="number"
          min={0}
          max={120}
          value={form.paymentTermDays}
          onChange={(e) => set({ paymentTermDays: e.target.value })}
        />
        {/*
          ⚠ Faturası kesilmiş bir ay olduktan sonra sunucu değişikliği reddeder
          (SNAK_FEE_BILLING_LOCKED) — bir ay ya iki kez ya da hiç faturalanırdı.
        */}
        <Select
          label="Aylık ücret ne zaman faturalansın?"
          hint="Peşinde snak'ın Ekim ekstresi (1 Kasım'da gelir) KASIM ücretini faturalar; komisyon her zaman sonradan. Faturası kesilmiş ay varken değiştirilemez."
          value={form.feeBilling}
          onChange={(e) => set({ feeBilling: e.target.value as SnakFeeBilling })}
        >
          {(Object.keys(SNAK_FEE_BILLING_LABELS) as SnakFeeBilling[]).map((m) => (
            <option key={m} value={m}>
              {SNAK_FEE_BILLING_LABELS[m]}
            </option>
          ))}
        </Select>
        {tenant && (
          <Select
            label="Durum"
            hint="Kapalı restoranın ekstresi reddedilir; daha önce bekletilmiş ayları da kesilmez."
            value={form.enabled ? 'on' : 'off'}
            onChange={(e) => set({ enabled: e.target.value === 'on' })}
          >
            <option value="on">Açık</option>
            <option value="off">Kapalı</option>
          </Select>
        )}
        {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
      </div>
    </Modal>
  );
}

function TermsModal({ tenant, onClose }: { tenant: SnakTenant | null; onClose: () => void }) {
  const add = useAddSnakTerms();
  const [form, setForm] = useState({
    effectiveFrom: '',
    billingMode: 'hybrid' as SnakBillingMode,
    monthlyFeeNet: '',
    listPriceNet: '',
    commissionPercent: '',
    vatMode: 'excluded' as SnakVatMode,
  });
  const [hata, setHata] = useState<string | null>(null);
  if (tenant === null) return null;

  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  return (
    <Modal
      open
      onClose={onClose}
      title={`${tenant.displayName} — sözleşme koşulu`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            loading={add.isPending}
            onClick={() => {
              setHata(null);
              add.mutate(
                {
                  tenantId: tenant.id,
                  effectiveFrom: form.effectiveFrom ? `${form.effectiveFrom}-01` : '',
                  billingMode: form.billingMode,
                  monthlyFeeNet: form.billingMode === 'commission' ? '0' : form.monthlyFeeNet,
                  commissionPercent: form.billingMode === 'flat' ? '0' : form.commissionPercent,
                  vatMode: form.vatMode,
                  listPriceNet:
                    form.billingMode === 'commission' || form.listPriceNet.trim() === ''
                      ? null
                      : form.listPriceNet.trim(),
                },
                { onSuccess: onClose, onError: (e: unknown) => setHata(errorMessage(e)) },
              );
            }}
          >
            Ekle
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-[var(--c-ink-soft)]">
          Koşul eklemelidir: eski satır geçmiş ayların faturasını açıklamaya devam eder. Faturası
          kesilmiş bir ayı geriye dönük değiştiren koşul reddedilir.
        </p>
        <Input
          label="Geçerli olduğu ilk ay"
          type="month"
          value={form.effectiveFrom}
          onChange={(e) => set({ effectiveFrom: e.target.value })}
        />
        <Select
          label="Faturalama modu"
          value={form.billingMode}
          onChange={(e) => set({ billingMode: e.target.value as SnakBillingMode })}
        >
          {(Object.keys(SNAK_BILLING_MODE_LABELS) as SnakBillingMode[]).map((m) => (
            <option key={m} value={m}>
              {SNAK_BILLING_MODE_LABELS[m]}
            </option>
          ))}
        </Select>
        {form.billingMode !== 'commission' && (
          <Input
            label="Aylık ücret (KDV hariç, €)"
            inputMode="decimal"
            placeholder="49.00"
            value={form.monthlyFeeNet}
            onChange={(e) => set({ monthlyFeeNet: e.target.value })}
          />
        )}
        {form.billingMode !== 'commission' && (
          <Input
            label="Liste fiyatı (KDV hariç, €) — isteğe bağlı"
            hint="Doluysa faturada ücret bu fiyatla basılır, aylık ücretle arasındaki fark BELGE İNDİRİMİ olarak görünür (örn. liste 179, ücret 100 → indirim 79)."
            inputMode="decimal"
            placeholder="179.00"
            value={form.listPriceNet}
            onChange={(e) => set({ listPriceNet: e.target.value })}
          />
        )}
        {form.billingMode !== 'flat' && (
          <>
            <Input
              label="Komisyon oranı (%)"
              inputMode="decimal"
              placeholder="3.00"
              value={form.commissionPercent}
              onChange={(e) => set({ commissionPercent: e.target.value })}
            />
            <Select
              label="Komisyon KDV kipi"
              value={form.vatMode}
              onChange={(e) => set({ vatMode: e.target.value as SnakVatMode })}
            >
              {(Object.keys(SNAK_VAT_MODE_LABELS) as SnakVatMode[]).map((m) => (
                <option key={m} value={m}>
                  {SNAK_VAT_MODE_LABELS[m]}
                </option>
              ))}
            </Select>
          </>
        )}
        {hata && <p className="text-xs text-[var(--c-danger)]">{hata}</p>}
      </div>
    </Modal>
  );
}
